/**
 * Turn a CRM GraphQL mutation failure into something a person can act on.
 *
 * The CRM's own edit surfaces — inline cell edit, the edit modals, the create
 * forms — write through pg_graphql/Apollo, NOT through an API route. So
 * `mapToUserFacingError` (which routes call) never ran for them, and the entity
 * hooks threw the raw Apollo message. A user who typed a phone number that
 * already belonged to someone saw:
 *
 *   Failed to update field: GraphQL error: CONTACT_METHOD_TAKEN: phone number
 *   "(901) 613-1510" already belongs to contact "Jane Doe" (9e70500b-d071-…)
 *
 * — the database's internal marker, a uuid, two stacked prefixes, and none of
 * the HINT explaining what to do.
 *
 * This is the transport adapter for that same mapper, NOT a second mapper.
 * `mapToUserFacingError` is pure and isomorphic (its only dependencies are the
 * contact-method and QuickBooks conflict parsers, both explicitly client-safe),
 * so the browser gets byte-identical wording to the API — including the
 * permission, foreign-key, missing-field and bad-format cases that a hand-rolled
 * client copy would have quietly omitted. One mapping, two transports, no drift.
 */

import { mapToUserFacingError } from "@/lib/utils/api/user-facing-errors";

/**
 * The code `mapToUserFacingError` returns when it recognized nothing. Its own
 * default sentence is phrased for an API operation ("We couldn't complete this
 * load contract."), so callers here substitute their own wording instead.
 */
const UNRECOGNIZED = "OPERATION_FAILED";

/** Errors already written FOR the user by our own guards, safe to show as-is. */
const PASSTHROUGH_MARKERS = [
  // enrollContact refusing a company-line anchor.
  "is a company line, not a person",
] as const;

function rawMessageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    return String((error as { message?: unknown }).message ?? "");
  }
  return "";
}

export interface CrmMutationErrorInfo {
  /** What to show the user. */
  message: string;
  /** `mapToUserFacingError`'s code, or UNRECOGNIZED. Lets a caller branch. */
  code: string;
}

/**
 * Map a mutation error to user-facing text plus its code.
 *
 * `fallback` is shown when nothing is recognized — pass something that names the
 * operation ("Couldn't save the contact."). The raw message is deliberately NOT
 * appended: an unrecognized Postgres error carries table, column and constraint
 * names, and USER_FACING_API_ERRORS applies on the client for the same reason it
 * applies to routes. Callers are expected to log the raw error separately.
 */
export function describeCrmMutationError(
  error: unknown,
  fallback: string
): CrmMutationErrorInfo {
  const raw = rawMessageOf(error);
  if (PASSTHROUGH_MARKERS.some((marker) => raw.includes(marker))) {
    return { message: raw, code: "GUARD_REFUSED" };
  }

  // `operation` only feeds the default sentence, which we discard in favour of
  // the caller's `fallback`.
  const mapped = mapToUserFacingError(error, { operation: "change" });
  return mapped.code === UNRECOGNIZED
    ? { message: fallback, code: UNRECOGNIZED }
    : { message: mapped.error, code: mapped.code };
}

/** Just the text, for callers that don't need the code. */
export function toUserFacingCrmMutationMessage(error: unknown, fallback: string): string {
  return describeCrmMutationError(error, fallback).message;
}

/**
 * An error whose `message` is already written for a human.
 *
 * The marker matters: a caller that wraps failures in its own prose ("Failed to
 * update field: …") has to know whether it is prefixing a technical string or a
 * complete explanation. Prefixing the latter produces "Failed to update field:
 * That phone number is already on “Jane Doe”…", which reads like two errors.
 */
export class CrmUserFacingError extends Error {
  readonly userFacing = true as const;
  /** `mapToUserFacingError`'s code (ACCESS_DENIED, CONTACT_METHOD_TAKEN, …). */
  readonly code: string;

  constructor(message: string, code: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "CrmUserFacingError";
    this.code = code;
  }
}

/** True when this error's message can be shown to the user as-is. */
export function isCrmUserFacingError(error: unknown): error is CrmUserFacingError {
  return error instanceof CrmUserFacingError;
}

/**
 * Build the Error an entity hook should throw on a failed mutation.
 *
 * Keeps the raw text reachable for logging/debugging on `cause` while the
 * `message` — the part that reaches a toast — stays user-facing.
 */
export function crmMutationError(error: unknown, fallback: string): CrmUserFacingError {
  const { message, code } = describeCrmMutationError(error, fallback);
  return new CrmUserFacingError(message, code, { cause: error });
}
